import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "@/lib/api";
import { useI18n } from "@/i18n/i18n";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, CheckCircle2, AlertCircle, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const REASONS = [
  "price",
  "no_time",
  "expectations_not_met",
  "changed_school",
  "achieved_goal",
  "teacher_or_group_issue",
  "other",
];

// Public axios client (no cookies/bearer)
const publicApi = axios.create({ baseURL: `${API_BASE}/api`, withCredentials: false });

export default function ChurnFeedback() {
  const { token } = useParams();
  const { t, lang, setLang } = useI18n();
  const [loading, setLoading] = useState(true);
  const [rec, setRec] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    publicApi
      .get(`/churn-feedback/${token}`)
      .then((r) => {
        if (cancelled) return;
        setRec(r.data);
        if (r.data?.lang && r.data.lang !== lang) setLang(r.data.lang);
        if (r.data?.submitted) setDone(true);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.status === 404 ? "invalid" : "unknown");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [token]);

  const toggle = (r) => {
    setSelected((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));
  };

  const onSubmit = async () => {
    if (!selected.length) {
      toast.error(t("churn_feedback_select_at_least_one"));
      return;
    }
    setSubmitting(true);
    try {
      await publicApi.post(`/churn-feedback/${token}`, { reasons: selected, comment });
      setDone(true);
    } catch (e) {
      const msg = e.response?.data?.detail;
      if (msg === "Feedback already submitted") {
        setDone(true);
      } else {
        toast.error(msg || "Error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error === "invalid" || !rec) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div data-testid="churn-invalid-card" className="max-w-md w-full bg-white border border-slate-200 rounded-xl p-8 text-center shadow-sm">
          <AlertCircle className="h-12 w-12 mx-auto text-amber-500 mb-3" />
          <h1 className="text-xl font-bold text-slate-900">{t("churn_feedback_invalid_link")}</h1>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Brand header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 bg-brand-blue text-white rounded-md flex items-center justify-center">
            <GraduationCap className="h-5 w-5" />
          </div>
          <div>
            <div className="font-display text-lg font-bold tracking-tight">WeSpeakSpanish</div>
          </div>
        </div>

        {done ? (
          <div data-testid="churn-thank-you-card" className="bg-white border border-slate-200 rounded-xl p-10 text-center shadow-sm">
            <CheckCircle2 className="h-14 w-14 mx-auto text-emerald-500 mb-4" />
            <h1 className="text-2xl font-bold text-slate-900 mb-3">{t("churn_feedback_submitted_title")}</h1>
            <p className="text-sm text-slate-600 leading-relaxed">{t("churn_feedback_submitted_msg")}</p>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-sm">
            <h1 className="text-2xl font-bold text-slate-900 mb-2" data-testid="churn-form-title">
              {t("churn_feedback_title")}
            </h1>
            <p className="text-sm text-slate-600 mb-6 leading-relaxed">
              {rec.student_name && (
                <span className="font-medium text-slate-800">
                  {lang === "es" ? `Hola ${rec.student_name}. ` : `Hi ${rec.student_name}. `}
                </span>
              )}
              {t("churn_feedback_intro")}
            </p>

            <div className="space-y-5">
              <div>
                <Label className="text-sm font-semibold text-slate-900">
                  {t("churn_feedback_reasons_label")}
                </Label>
                <div className="mt-3 space-y-2.5">
                  {REASONS.map((r) => (
                    <label
                      key={r}
                      data-testid={`churn-reason-${r}`}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        selected.includes(r)
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                    >
                      <Checkbox
                        checked={selected.includes(r)}
                        onCheckedChange={() => toggle(r)}
                        className="mt-0.5"
                      />
                      <span className="text-sm text-slate-700 leading-snug">{t(`churn_reason_${r}`)}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-semibold text-slate-900" htmlFor="churn-comment">
                  {t("churn_feedback_comment_label")}
                </Label>
                <Textarea
                  id="churn-comment"
                  data-testid="churn-comment-input"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t("churn_feedback_comment_placeholder")}
                  rows={4}
                  className="mt-2 resize-none"
                  maxLength={1000}
                />
              </div>

              <Button
                data-testid="churn-submit-btn"
                onClick={onSubmit}
                disabled={submitting}
                className="w-full bg-brand-blue hover:bg-brand-blue/90 text-white"
                size="lg"
              >
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {t("churn_feedback_submit")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
